import React, { useState, useEffect } from 'react';
import { Form, Table, Button, Alert } from 'react-bootstrap';
import { fetchCaseFiles, fetchProcessedBankStatements, fetchGlEntries } from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';
import { ProcessedBankStatement } from '../../constants/constants';
import { formatDate } from '../../utils/dateFormatters';

interface GLBankReconciliationInputsProps {
  case_id: string;
  onChange: (data: GLBankReconciliationInputData) => void;
  validated: boolean;
}

export interface GLBankReconciliationInputData {
  gl_file_id: string;
  gl_account_name: string;
  statement_ids: string[];
  date_range: { from: string; to: string };
}

interface CaseFile {
  file_id: string;
  filename: string;
  file_type: string;
  gl_processing_status?: string;
  gl_entry_count?: number;
}

interface GLAccount {
  account_name: string;
  account_code?: string;
  account_type?: string;
  entry_count: number;
}

const parseISODate = (iso: string | null | undefined): Date => {
  if (!iso) return new Date(0);
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
};
const formatISO = (d: Date): string => d.toISOString().split('T')[0];
const firstOfMonth = (d: Date): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
const lastOfMonth = (d: Date): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));

const GLBankReconciliationInputs: React.FC<GLBankReconciliationInputsProps> = ({
  case_id,
  onChange,
  validated,
}) => {
  const auth = useAuth();

  // GL side
  const [glFiles, setGlFiles] = useState<CaseFile[]>([]);
  const [selectedGlFileId, setSelectedGlFileId] = useState('');
  const [glAccounts, setGlAccounts] = useState<GLAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedGlAccountName, setSelectedGlAccountName] = useState('');

  // Bank side
  const [statements, setStatements] = useState<ProcessedBankStatement[]>([]);
  const [selectedStatementIds, setSelectedStatementIds] = useState<string[]>([]);

  // Date range
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  // Loading / error
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Initial load: GL files + bank statements ───────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [filesResp, stmtsResp] = await Promise.all([
          fetchCaseFiles(case_id, auth.user?.id_token),
          fetchProcessedBankStatements(case_id, auth.user?.id_token),
        ]);

        const allFiles: CaseFile[] = filesResp.data?.files ?? filesResp.data ?? [];
        const glOnly = allFiles.filter(
          (f) => f.file_type === 'general_ledger' &&
                 (f.gl_processing_status === 'complete' || f.gl_processing_status === 'completed')
        );
        setGlFiles(glOnly);

        const sortedStmts = (stmtsResp.data?.statements ?? []).sort(
          (a: ProcessedBankStatement, b: ProcessedBankStatement) =>
            parseISODate(a.statement_date).getTime() - parseISODate(b.statement_date).getTime()
        );
        setStatements(sortedStmts);

        // Auto-set date range from statement span
        if (sortedStmts.length > 0) {
          const first = parseISODate(sortedStmts[0].statement_date);
          const last = parseISODate(sortedStmts[sortedStmts.length - 1].statement_date);
          setDateRange({
            from: formatISO(firstOfMonth(first)),
            to: formatISO(lastOfMonth(last)),
          });
        }
      } catch (err) {
        setError('Failed to load case files or bank statements');
        console.error(err);
      } finally {
        setLoadingFiles(false);
      }
    };
    load();
  }, [case_id, auth.user?.id_token]);

  // ── Load GL accounts when a GL file is chosen ──────────────────────────
  useEffect(() => {
    if (!selectedGlFileId) {
      setGlAccounts([]);
      setSelectedGlAccountName('');
      return;
    }
    const loadAccounts = async () => {
      setLoadingAccounts(true);
      try {
        const resp = await fetchGlEntries(case_id, selectedGlFileId, auth.user?.id_token);
        const entries: any[] = resp.data?.gl_entries ?? [];
        // Deduplicate by account_name
        const map = new Map<string, GLAccount>();
        for (const entry of entries) {
          const name = entry.account_name || '(unknown)';
          if (map.has(name)) {
            map.get(name)!.entry_count++;
          } else {
            map.set(name, {
              account_name: name,
              account_code: entry.account_code,
              account_type: entry.account_type,
              entry_count: 1,
            });
          }
        }
        const accounts = Array.from(map.values()).sort((a, b) =>
          a.account_name.localeCompare(b.account_name)
        );
        setGlAccounts(accounts);
        setSelectedGlAccountName('');
      } catch (err) {
        setError('Failed to load GL accounts');
        console.error(err);
      } finally {
        setLoadingAccounts(false);
      }
    };
    loadAccounts();
  }, [selectedGlFileId, case_id, auth.user?.id_token]);

  // ── Propagate changes upward ───────────────────────────────────────────
  useEffect(() => {
    onChange({
      gl_file_id: selectedGlFileId,
      gl_account_name: selectedGlAccountName,
      statement_ids: selectedStatementIds,
      date_range: dateRange,
    });
  }, [selectedGlFileId, selectedGlAccountName, selectedStatementIds, dateRange]);

  const toggleStatement = (id: string) => {
    setSelectedStatementIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const selectAllStatements = () => {
    setSelectedStatementIds(statements.map((s) => s.statement_id));
  };

  if (loadingFiles) return <div>Loading files…</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  const isGlFileValid = !!selectedGlFileId;
  const isGlAccountValid = !!selectedGlAccountName;
  const isStatementsValid = selectedStatementIds.length > 0;

  return (
    <div>
      {/* ── GL File selector ── */}
      <Form.Group className="mb-3">
        <Form.Label>General Ledger File</Form.Label>
        {glFiles.length === 0 ? (
          <Alert variant="warning" className="mb-0">
            No processed general ledger files found for this case.
          </Alert>
        ) : (
          <Form.Select
            value={selectedGlFileId}
            onChange={(e) => setSelectedGlFileId(e.target.value)}
            isInvalid={validated && !isGlFileValid}
          >
            <option value="">— Select a GL file —</option>
            {glFiles.map((f) => (
              <option key={f.file_id} value={f.file_id}>
                {f.filename}
                {f.gl_entry_count != null ? ` (${f.gl_entry_count} entries)` : ''}
              </option>
            ))}
          </Form.Select>
        )}
        <Form.Control.Feedback type="invalid">
          Please select a general ledger file.
        </Form.Control.Feedback>
      </Form.Group>

      {/* ── GL Account selector ── */}
      {selectedGlFileId && (
        <Form.Group className="mb-3">
          <Form.Label>GL Account to Reconcile</Form.Label>
          {loadingAccounts ? (
            <div className="text-muted">Loading accounts…</div>
          ) : glAccounts.length === 0 ? (
            <Alert variant="warning" className="mb-0">No accounts found in this GL file.</Alert>
          ) : (
            <Form.Select
              value={selectedGlAccountName}
              onChange={(e) => setSelectedGlAccountName(e.target.value)}
              isInvalid={validated && !isGlAccountValid}
            >
              <option value="">— Select a GL account —</option>
              {glAccounts.map((acct) => (
                <option key={acct.account_name} value={acct.account_name}>
                  {acct.account_name}
                  {acct.account_type ? ` [${acct.account_type}]` : ''}
                  {` — ${acct.entry_count} entries`}
                </option>
              ))}
            </Form.Select>
          )}
          <Form.Control.Feedback type="invalid">
            Please select a GL account.
          </Form.Control.Feedback>
        </Form.Group>
      )}

      {/* ── Date range ── */}
      <Form.Group className="mb-3">
        <Form.Label>Analysis Date Range</Form.Label>
        <div className="row g-2">
          <div className="col">
            <Form.Control
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              isInvalid={validated && !dateRange.from}
            />
          </div>
          <div className="col">
            <Form.Control
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              isInvalid={validated && !dateRange.to}
            />
          </div>
        </div>
      </Form.Group>

      {/* ── Bank statement selector ── */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="mb-0">Bank Statements to Reconcile Against</Form.Label>
          {statements.length > 0 && (
            <Button variant="link" size="sm" className="p-0" onClick={selectAllStatements}>
              Select all
            </Button>
          )}
        </div>
        {statements.length === 0 ? (
          <Alert variant="warning">No processed bank statements found for this case.</Alert>
        ) : (
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Bank</th>
                <th>Account</th>
                <th>Period</th>
              </tr>
            </thead>
            <tbody>
              {statements.map((stmt) => {
                const checked = selectedStatementIds.includes(stmt.statement_id);
                return (
                  <tr
                    key={stmt.statement_id}
                    className={checked ? 'table-primary' : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleStatement(stmt.statement_id)}
                  >
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStatement(stmt.statement_id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>{stmt.bank_name}</td>
                    <td>…{stmt.account_number}</td>
                    <td>{formatDate(stmt.statement_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {validated && !isStatementsValid && (
          <div className="text-danger small mt-1">
            Please select at least one bank statement.
          </div>
        )}
      </div>
    </div>
  );
};

export default GLBankReconciliationInputs;
